import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom'; // Import useNavigate hook
import { motion } from 'framer-motion';
import { FaCheckCircle, FaExclamationCircle } from 'react-icons/fa';
import TabbedFormComponent from '../common/TabbedFormComponent';
import AdvancedDataTableComponent from '../common/AdvancedDataTableComponent';
import { fetchClientData } from '../../services/adminApiService';
import { registerRole, fetchRoleDataById, updateRole, fetchRoleData } from '../../services/roleApiService'; // Adjust the path as necessary
import FloatingLabelInput from '../ui/FloatingLabelInput';
import FloatingLabelSelect from '../ui/FloatingLabelSelect';


const RoleForm = ({ selectedRole, onRoleUpdated }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [apiResponse, setApiResponse] = useState(null);
    const [loading, setLoading] = useState(false);
    const [clients, setClients] = useState([]);
    const [countriesLoaded, setCountriesLoaded] = useState(false); // Track if countries are loaded

    const [formValues, setFormValues] = useState({
        clientId: '',
        roleId: '',
        roleName: ''
    });

    const [errors, setErrors] = useState({});

    // Simple validation function
    const validate = () => {
        const newErrors = {};
        if (!formValues.roleName) newErrors.roleName = 'Role Name is required';
        if (!formValues.clientId) newErrors.clientId = 'Organization is required';

        return newErrors;
    };

    const handleBlur = (field) => {
        const validationErrors = validate();
        setErrors(validationErrors);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormValues({ ...formValues, [name]: value });
        // Validate each field in real-time
        //const validationErrors = validate(); // Re-validate all fields
        //setErrors({ ...errors, [name]: '' }); // Clear error for the field being changed
        // setErrors(validationErrors); // Update errors state with any validation messages

    };

    const getClients = async () => {
        try {
            const clientData = await fetchClientData();
            setClients(clientData.map(client => ({ value: client.clientId, label: client.clientName })));
        } catch (error) {
            console.error("Error fetching countries:", error);
        }
    };

    const inputClasses = (field) =>
        errors[field] ? 'border-red-500' : 'border-gray-300';


    useEffect(() => {
        getClients();
    }, []);


    useEffect(() => {
        const timer = setTimeout(() => {
            if (selectedRole && selectedRole.RoleId != "") {
                setIsEditing(true);
                setFormValues({
                    clientId: selectedRole.clientId || '',
                    roleId: selectedRole.roleId || '',
                    roleName: selectedRole.roleName || ''
                });
                setFormValues(prevValues => ({
                    ...prevValues,
                    clientId: selectedRole.clientId,
                }));
            } else {
                setIsEditing(false);
            }
        }, 500); // 3000ms = 3 seconds

        return () => clearTimeout(timer);
    }, [selectedRole, clients]); // Ensure to re-run when countries change



    const handleSubmit = async (e) => {

        e.preventDefault();
        const validationErrors = validate();
        if (Object.keys(validationErrors).length === 0) {
            try {
                //console.log('Form submitted', formValues);
                setLoading(true);
                let data;
                if (isEditing) {
                    data = await updateRole(formValues);
                } else {
                    data = await registerRole(formValues);
                }

                setApiResponse(data);
                if (!data.error) {
                    resetFormValues();
                    onRoleUpdated(null);
                    setIsEditing(false);
                   // setClients([]);
                } else {
                    return;
                }
            } catch (err) {
                const validationErrors = {};
                err.inner.forEach(error => {
                    validationErrors[error.path] = error.message;
                });
                setErrors(validationErrors);
                setApiResponse({ err: 'Failed to submit form. Please try again.' });
            } finally {
                setLoading(false);
            }
            setErrors({});
        } else {
            setErrors(validationErrors);
        }
    };
    const resetFormValues = () => {
        setFormValues({
            clientId: '',
            roleId: '',
            roleName: ''
        });
        setIsEditing(false);
        setErrors({});
        onRoleUpdated(null);
        //setApiResponse(null);
    }
    const navigate = useNavigate(); // Initialize useNavigate

    const handleCancel = () => {
        resetFormValues();
        onRoleUpdated(null);
        // navigate('/clientRegistration'); // Replace with your desired URL
        // window.location.reload();
    };
    return (
        <div className="container mx-auto p-6 bg-white rounded-lg border">
            <form onSubmit={handleSubmit} className="space-y-6 w-full">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {isEditing ?
                        <input
                            type="hidden"
                            name="roleId"
                            value={formValues.roleId}
                            id="roleId"
                            autoComplete="off"
                            readOnly={true}
                            onChange={handleChange}
                        /> : ''
                    }

                    

                    <div>
                        {loading ? (
                            <p>Loading countries...</p>
                        ) : (
                            <FloatingLabelSelect
                                label="Select Organization"
                                id="clientId"
                                name="clientId"
                                value={clients.find(client => client.value === formValues.clientId) || null} // Handle no selection case with null
                                onChange={(option) => setFormValues({ ...formValues, clientId: option.value })}
                                onBlur={() => handleBlur('clientId')}
                                className={inputClasses('clientId')}
                                options={clients}
                                isSearchable={true}
                            />
                        )}
                        {errors.clientId && <div className="text-red-500 text-sm">{errors.clientId}</div>}
                    </div>
                    <div>
                        <FloatingLabelInput
                            label="Role"
                            id="roleName"
                            name="roleName"
                            value={formValues.roleName}
                            onChange={handleChange}
                            onBlur={() => handleBlur('roleName')}
                            className={inputClasses('roleName')}

                        />
                        {errors.roleName && <div className="text-red-500 text-sm">{errors.roleName}</div>}
                    </div>
                </div>


                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="mt-4">
                        <button
                            type="submit"
                            className={`px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 ${loading ? 'opacity-50' : ''}`}
                            disabled={loading}
                        >
                            {loading ? 'Submitting...' : selectedRole ? 'Update' : 'Submit'}
                        </button>
                    </div>
                    <div className="mt-4">
                        <button
                            type="button"
                            onClick={handleCancel} // This is how you reset the form
                            className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600"
                        >
                            Cancel
                        </button>
                    </div>
                    <div className="mt-4"></div>
                </div>
                {apiResponse && (
                    <motion.div
                        className={`mt-4 flex items-center ${apiResponse.error ? 'text-red-500' : 'text-green-500'}`}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                    >
                        {apiResponse.error ? (
                            <>
                                <FaExclamationCircle className="mr-2" />
                                {apiResponse.error}
                            </>
                        ) : (
                            <>
                                <FaCheckCircle className="mr-2" />
                                {apiResponse.success}
                            </>
                        )}
                    </motion.div>
                )}
            </form>
        </div>
    );
};

const RoleListTable = ({ onSelectRole, switchToFormTab }) => {
    const tableHeaders = ["id", "Organization Name", "Role Name", "CreatedAt"];

    const [list, setList] = useState([]);  // Store Clients

    const [loading, setLoading] = useState(false);

    // Fetch Clients when the component loads
    useEffect(() => {
        const getData = async (cleintId) => {
            try {
                const data = await fetchRoleData(localStorage.getItem('clientID'));  // Fetch countries from service
                const options = data.map((role) => ({
                    id: role.roleId,
                    roleName: role.roleName,
                    clientId: role.clientId,
                    clientName: role.clientName,
                    createdAt: role.createdAt
                }));
                setList(options);  // Set the countries as options for the select dropdown

            } catch (error) {
                console.error("Error fetching countries:", error);
            } finally {
                // setLoadingCountries(false);  // Stop loading spinner
            }
        };

        getData();
    }, []);


    // Mapping of headers to data keys
    const headerMapping = {
        "id": "id",
        "Organization Name": "clientName", // Maps to the data key 'clientName'
        "Role Name": "roleName", // Assuming you have 'email' key in your data. If not, replace accordingly
        "CreatedAt": "createdAt" // Maps to the data key 'createdAt'
    };


    const handleEdit = async (rowObject) => {
        try {
            setLoading(true); // Show loading spinner

            // Fetch client data based on the row object (e.g., client ID)
            const role = await fetchRoleDataById(rowObject.id);
            var roleData = ({
                roleId: role.roleId,
                roleName: role.roleName,
                clientId: role.clientId,
                clientName: role.clientName,
                createdAt: role.createdAt

            });
            // Populate the form with the fetched client data
            onSelectRole(roleData);

            // Switch to the form tab
            switchToFormTab();

        } catch (error) {
            console.error('Error fetching client data:', error);
        } finally {
            setLoading(false); // Hide loading spinner
        }
    };


    return (
        <div className="container mx-auto p-6 bg-white rounded-lg border">
            <AdvancedDataTableComponent
                headers={tableHeaders}
                data={list}
                headerMapping={headerMapping}
                onEdit={handleEdit}
                showActions={true}
            />
        </div>
    );

};


const TestPage = () => {

    const [selectedRole, setSelectedRole] = useState(null); // Track selected client for editing
    const [activeTab, setActiveTab] = useState(0); // Track active tab

    const handleEdit = (role) => {
        setSelectedRole(role);
        setActiveTab(0);
    };
    // Callback to clear the selected Role
    const handleRoleUpdated = () => {
        setSelectedRole(null);
    };
    return (<TabbedFormComponent
        formContent={(switchToFormTab) => (
            <RoleForm
                selectedRole={selectedRole}
                onRoleUpdated={handleRoleUpdated}
                switchToFormTab={switchToFormTab}
            />
        )}
        dataTableComponent={(switchToFormTab) => (
            <RoleListTable
                onSelectRole={setSelectedRole}
                switchToFormTab={switchToFormTab}
            />
        )}
    />)
};

export default TestPage;
